import React, { useState } from 'react';
import {
  MARKET_PRICES,
  CURRENT_RATES,
  PROTOCOL_CONSTANTS,
  calcDecision,
  calcExactGain
} from '../constants/pricing';
import {
  fmtIcp,
  fmtN
} from '../utils/formatters';
import StakingModal from './StakingModal';

// Utility function to format vs initial comparison
const formatVsInitial = (value, compareAmount) => {
  const diff = value - compareAmount;
  if (Math.abs(diff) < 0.01) {
    return <span className="break-even">≈0 (break-even)</span>;
  }
  return (
    <span className={diff >= 0 ? 'gain-inline' : 'loss-inline'}>
      ({fmtIcp(diff)} vs initial)
    </span>
  );
};

const DecisionModeWidget = ({ onClose, userICP = 0 }) => {
  const [compareAmount, setCompareAmount] = useState(100);
  const [isStakingModalOpen, setIsStakingModalOpen] = useState(false);

  // Use current rates and prices
  const icpPrice = MARKET_PRICES.ICP;
  const wtnPrice = MARKET_PRICES.WTN;
  const wtnPer100 = PROTOCOL_CONSTANTS.WTN_PER_100_ICP;

  // User state detection
  const hasICP = userICP > 0;
  const availableToSwap = userICP * icpPrice;

  // Calculate decision comparison results
  const decisionResults = calcDecision(
    compareAmount, 
    CURRENT_RATES, 
    wtnPer100, 
    wtnPrice, 
    icpPrice
  );
  const decisionExactGain = calcExactGain(decisionResults.market.nIcp, CURRENT_RATES);

  // Action handlers
  const handleStakeWithWaterNeuron = () => {
    if (!hasICP) {
      alert('You need ICP to stake. Please get ICP first.');
      return;
    }
    setIsStakingModalOpen(true);
  };

  const handleSwapToNICP = () => {
    if (!hasICP) {
      alert('You need ICP to swap. Please get ICP first.');
      return;
    }
    // TODO: Implement swap flow
    console.log('Swap to nICP:', compareAmount, 'ICP');
  };


  return (
    <div className="decision-mode-widget">

      {/* Input Section */}
      <div className="comparison-input">
        <label className="input-label">Calculate what you get for</label>
        <div className="input-container">
          <input
            type="number"
            value={compareAmount}
            onChange={(e) => setCompareAmount(Number(e.target.value))}
            className="comparison-amount-input"
            min="0"
            step="0.1"
          />
          <span className="input-suffix">ICP</span>
        </div>
      </div>


      {/* Option Cards */}
        <div className="option-cards">
          
          {/* Stake Card */}
          <div className="option-card">
            <div className="card-header">
              <div className="header-title-group">
                <h3>STAKE</h3>
                <div className="header-amount">{fmtN(decisionResults.stake.nIcp)} nICP</div>
              </div>
              
            </div>
            
            <div className="card-content">
              
              <div className="card-section">
                <div className="section-label">Future WTN Airdrop</div>
                <div className="section-value">+{decisionResults.stake.wtnCount.toFixed(0)} WTN ≈ {fmtIcp(decisionResults.stake.wtnIcpBonus)} ICP <span className="bonus-pill">BONUS</span></div>
              </div>
              
              <div className="card-section">
                <div className="section-label">Delayed unstake (6mo)</div>
                <div className={`section-value-main-secondary ${decisionResults.stake.nIcp * CURRENT_RATES.nIcpNavOverIcp - compareAmount > 0 ? 'positive-gain' : ''}`}>~{fmtIcp(decisionResults.stake.nIcp * CURRENT_RATES.nIcpNavOverIcp)} ICP</div>
                <div className="section-note">{formatVsInitial(decisionResults.stake.nIcp * CURRENT_RATES.nIcpNavOverIcp, compareAmount)}</div>
              </div>

              {/* Action Button */}
              <div className="card-action">
                <button 
                  className="action-button primary"
                  onClick={handleStakeWithWaterNeuron}
                >
                  Stake ICP with WaterNeuron
                </button>
                {hasICP ? (
                  <div className="available-stake-info">
                    Available for stake: <strong>{userICP.toFixed(2)} ICP</strong>
                  </div>
                ) : (
                  <div className="get-icp-first-info">
                    ICP balance is 0. Please get some ICP first
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Market Card */}
          <div className={`option-card ${decisionExactGain.isSignificant && decisionExactGain.isPositive ? 'recommended' : ''}`}>
            <div className="card-header">
              <div className="header-title-group">
                <h3>MARKET SWAP</h3>
                <div className="header-amount">{fmtN(decisionResults.market.nIcp)} nICP</div>
              </div>
              <span className="badge badge-neutral">No airdrop</span>
            </div>
            
            <div className="card-content">
              
              <div className="card-section">
                <div className="section-label">Future WTN Airdrop</div>
                <div className="section-value">NO AIRDROP</div>
              </div>
              
              <div className="card-section">
                <div className="section-label">Delayed unstake (6mo)</div>
                <div className={`section-value-main-secondary ${decisionExactGain.delayed - compareAmount > 0 ? 'positive-gain' : ''}`}>~{fmtIcp(decisionExactGain.delayed)} ICP</div>
                <div className="section-note">
                  {formatVsInitial(decisionExactGain.delayed, compareAmount)}
                  {decisionExactGain.delayed - compareAmount > 0.01 && (
                    <span className="gain-inline" style={{ marginLeft: '4px' }}>
                      , ≈{decisionExactGain.apy.toFixed(1)}% APY
                    </span>
                  )}
                </div>
              </div>
              
              {decisionExactGain.isSignificant && decisionExactGain.isPositive && (
                <div className="best-option-card">
                  ✅ <strong>Best option:</strong> Buy on market & delay unstake → gain <strong>+{fmtIcp(decisionExactGain.gainIcp)} ICP</strong> in 6 months (~{decisionExactGain.apy.toFixed(1)}% APY). Or hold and earn 15% yearly on the nICP growth.
                </div>
              )}

              {/* Action Button */}
              <div className="card-action">
                <button 
                  className="action-button primary"
                  onClick={handleSwapToNICP}
                >
                  Swap to nICP
                </button>
                {hasICP ? (
                  <div className="available-swap-info">
                    Available to swap: <strong>${availableToSwap.toFixed(2)}</strong>
                  </div>
                ) : (
                  <div className="get-icp-first-info">
                    Your wallet is empty
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

              {/* Details Accordion */}
        <details className="details-accordion">
          <summary className="details-summary">Details</summary>
          <ul className="details-list">
            <li>Market rate (ICP→nICP): {fmtN(CURRENT_RATES.icpToNICP_market)}</li>
            <li>NAV (nICP→ICP): {fmtN(CURRENT_RATES.nIcpNavOverIcp)}</li>
            <li>Assumed slippage/fees: {PROTOCOL_CONSTANTS.MARKET_SLIPPAGE_FEE}%</li>
          </ul>
        </details>

        {/* Staking Modal */}
        <StakingModal
          isOpen={isStakingModalOpen}
          onClose={() => setIsStakingModalOpen(false)}
          userICP={userICP}
        />
      </div>
    );
  };

  export default DecisionModeWidget;
